import { useCallback, useEffect, useRef, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../../api/staff'
import type { AuthSession } from '../../types/plans'
import {
  workspaceCardClassName,
  workspaceEmptyClassName,
  workspaceInputClassName,
  workspacePanelClassName,
} from '../workspaceStyles'

// ── Types ──────────────────────────────────────────────────────────────────

export interface MediaFolder {
  id: number
  parent_id: number | null
  name: string
  path: string
  media_count: number
  children: MediaFolder[]
}

export interface MediaItem {
  id: number
  folder_id: number | null
  filename: string
  original_filename: string
  file_type: 'image' | 'video' | 'document'
  mime_type: string
  size: number
  width: number | null
  height: number | null
  alt_text: string | null
  description: string | null
  created_at: string
  url: string
  large_url: string | null
  medium_url: string | null
  thumb_url: string | null
}

// ── Helpers ────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`
}

function fileTypeLabel(type: string): string {
  if (type === 'video') return 'Видео'
  if (type === 'document') return 'Документ'
  return 'Изображение'
}

// ── Folder tree node ───────────────────────────────────────────────────────

function FolderNode({
  folder,
  selectedId,
  onSelect,
  onRename,
  onDelete,
  onAddChild,
}: {
  folder: MediaFolder
  selectedId: number | null
  onSelect: (id: number | null) => void
  onRename: (id: number, name: string) => void
  onDelete: (id: number) => void
  onAddChild: (parentId: number) => void
}) {
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState(folder.name)

  const isSelected = selectedId === folder.id

  return (
    <li>
      <div
        className={`group flex items-center gap-1 rounded-xl px-2 py-1.5 cursor-pointer transition-colors ${
          isSelected ? 'bg-sky-50 text-sky-700' : 'text-slate-700 hover:bg-slate-50'
        }`}
      >
        <button
          type="button"
          className="flex-none w-4 text-slate-400"
          onClick={() => setOpen((p) => !p)}
        >
          {folder.children.length > 0 ? (open ? '▾' : '▸') : <span className="opacity-0">▸</span>}
        </button>

        {editing ? (
          <input
            className="flex-1 rounded-lg border border-sky-300 px-2 py-0.5 text-sm"
            value={editName}
            autoFocus
            onChange={(e) => setEditName(e.target.value)}
            onBlur={() => {
              if (editName.trim()) onRename(folder.id, editName.trim())
              setEditing(false)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && editName.trim()) { onRename(folder.id, editName.trim()); setEditing(false) }
              if (e.key === 'Escape') setEditing(false)
            }}
          />
        ) : (
          <span
            className="flex-1 min-w-0 truncate text-sm"
            onClick={() => onSelect(folder.id)}
          >
            📁 {folder.name}
            <span className="ml-1.5 text-xs text-slate-400">{folder.media_count}</span>
          </span>
        )}

        <div className="hidden group-hover:flex items-center gap-0.5 ml-auto">
          <button type="button" title="Добавить подпапку" className="rounded px-1 py-0.5 text-xs text-slate-400 hover:text-sky-600" onClick={() => { setOpen(true); onAddChild(folder.id) }}>+</button>
          <button type="button" title="Переименовать" className="rounded px-1 py-0.5 text-xs text-slate-400 hover:text-amber-600" onClick={() => { setEditing(true); setEditName(folder.name) }}>✎</button>
          <button type="button" title="Удалить" className="rounded px-1 py-0.5 text-xs text-slate-400 hover:text-rose-600" onClick={() => onDelete(folder.id)}>✕</button>
        </div>
      </div>

      {open && folder.children.length > 0 && (
        <ul className="ml-4 mt-0.5">
          {folder.children.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              selectedId={selectedId}
              onSelect={onSelect}
              onRename={onRename}
              onDelete={onDelete}
              onAddChild={onAddChild}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

// ── Upload progress overlay ────────────────────────────────────────────────

function UploadProgressBar({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
        <div
          className="h-2 rounded-full bg-sky-500 transition-all"
          style={{ width: `${total > 0 ? Math.round((current / total) * 100) : 0}%` }}
        />
      </div>
      <span className="text-xs text-slate-500">{current} / {total}</span>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────

export function StaffMediaSection({
  bootstrap,
  session,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
}) {
  const [folders, setFolders] = useState<MediaFolder[]>([])
  const [selectedFolderId, setSelectedFolderId] = useState<number | null | 'all'>('all')
  const [items, setItems] = useState<MediaItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [fileTypeFilter, setFileTypeFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 })
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [activeItem, setActiveItem] = useState<MediaItem | null>(null)
  const [editingItem, setEditingItem] = useState<{ altText: string; description: string } | null>(null)
  const [dragging, setDragging] = useState(false)
  const [newFolderParent, setNewFolderParent] = useState<number | null | false>(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [copied, setCopied] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const PER_PAGE = 48

  // ── Load folders ──────────────────────────────────────────────────────────

  const loadFolders = useCallback(async () => {
    try {
      const data = await staffApi.mediaGetFolders(bootstrap, session)
      setFolders(data)
    } catch { /* ignore */ }
  }, [bootstrap, session])

  useEffect(() => { void loadFolders() }, [loadFolders])

  // ── Debounce search ───────────────────────────────────────────────────────

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  // ── Load media ────────────────────────────────────────────────────────────

  const loadMedia = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, unknown> = { page, per_page: PER_PAGE }
      if (selectedFolderId !== 'all') params.folder_id = selectedFolderId ?? ''
      if (debouncedSearch) params.search = debouncedSearch
      if (fileTypeFilter) params.file_type = fileTypeFilter
      const data = await staffApi.mediaList(bootstrap, session, params)
      setItems(data.items as MediaItem[])
      setTotal(data.total)
    } catch { setItems([]) } finally { setLoading(false) }
  }, [bootstrap, session, page, selectedFolderId, debouncedSearch, fileTypeFilter])

  useEffect(() => { void loadMedia() }, [loadMedia])

  // Reset page when filters change
  useEffect(() => { setPage(1) }, [selectedFolderId, debouncedSearch, fileTypeFilter])

  // ── Upload ────────────────────────────────────────────────────────────────

  async function handleFiles(files: File[]) {
    if (!files.length) return
    setUploading(true)
    setUploadProgress({ current: 0, total: files.length })
    let done = 0
    for (const file of files) {
      try {
        const folderId = selectedFolderId !== 'all' ? selectedFolderId : null
        await staffApi.mediaUpload(bootstrap, session, file, folderId)
      } catch { /* skip bad file */ }
      done++
      setUploadProgress({ current: done, total: files.length })
    }
    setUploading(false)
    await loadMedia()
    await loadFolders()
  }

  function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    void handleFiles(files)
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    const files = Array.from(e.dataTransfer.files)
    void handleFiles(files)
  }

  // ── Selection ─────────────────────────────────────────────────────────────

  function toggleSelect(id: number, e: React.MouseEvent) {
    e.stopPropagation()
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id); } else { next.add(id) }
      return next
    })
  }

  function selectAll() { setSelectedIds(new Set(items.map((i) => i.id))) }
  function clearSelection() { setSelectedIds(new Set()) }

  // ── Bulk delete ───────────────────────────────────────────────────────────

  async function bulkDelete() {
    if (!selectedIds.size) return
    if (!confirm(`Удалить ${selectedIds.size} файл(ов)?`)) return
    await staffApi.mediaDelete(bootstrap, session, [...selectedIds])
    setSelectedIds(new Set())
    if (activeItem && selectedIds.has(activeItem.id)) setActiveItem(null)
    await loadMedia()
    await loadFolders()
  }

  // ── Single delete ─────────────────────────────────────────────────────────

  async function deleteItem(item: MediaItem) {
    if (!confirm(`Удалить «${item.original_filename}»?`)) return
    await staffApi.mediaDelete(bootstrap, session, [item.id])
    if (activeItem?.id === item.id) setActiveItem(null)
    await loadMedia()
    await loadFolders()
  }

  // ── Save edit ─────────────────────────────────────────────────────────────

  async function saveEdit() {
    if (!activeItem || !editingItem) return
    await staffApi.mediaUpdate(bootstrap, session, activeItem.id, {
      alt_text: editingItem.altText,
      description: editingItem.description,
    })
    setEditingItem(null)
    await loadMedia()
  }

  // ── Folder actions ────────────────────────────────────────────────────────

  async function handleCreateFolder() {
    if (!newFolderName.trim()) return
    await staffApi.mediaCreateFolder(bootstrap, session, newFolderName.trim(), newFolderParent === false ? null : newFolderParent)
    setNewFolderName('')
    setNewFolderParent(false)
    await loadFolders()
  }

  async function handleRenameFolder(id: number, name: string) {
    await staffApi.mediaUpdateFolder(bootstrap, session, id, name)
    await loadFolders()
  }

  async function handleDeleteFolder(id: number) {
    if (!confirm('Удалить папку? Файлы останутся без папки.')) return
    await staffApi.mediaDeleteFolder(bootstrap, session, id)
    if (selectedFolderId === id) setSelectedFolderId('all')
    await loadFolders()
    await loadMedia()
  }

  // ── Copy URL ──────────────────────────────────────────────────────────────

  function copyUrl(url: string, key: string) {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(key)
      setTimeout(() => setCopied(null), 1500)
    })
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(total / PER_PAGE)

  return (
    <div className="grid gap-4 p-4 lg:grid-cols-[260px_1fr_280px]">
      {/* Left: folder tree */}
      <aside className={workspacePanelClassName + ' self-start'}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Папки</span>
          <button
            type="button"
            className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600 hover:bg-sky-50 hover:text-sky-700 transition"
            onClick={() => setNewFolderParent(null)}
          >
            + Папка
          </button>
        </div>

        {newFolderParent !== false && (
          <div className="flex gap-2 mt-2">
            <input
              className="flex-1 rounded-xl border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-sky-300"
              placeholder="Название папки"
              value={newFolderName}
              autoFocus
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleCreateFolder()
                if (e.key === 'Escape') { setNewFolderParent(false); setNewFolderName('') }
              }}
            />
            <button type="button" className="text-xs text-sky-600 hover:underline" onClick={() => void handleCreateFolder()}>ОК</button>
          </div>
        )}

        <ul className="mt-2 space-y-0.5">
          <li>
            <button
              type="button"
              className={`w-full text-left rounded-xl px-2 py-1.5 text-sm transition-colors ${selectedFolderId === 'all' ? 'bg-sky-50 text-sky-700' : 'text-slate-700 hover:bg-slate-50'}`}
              onClick={() => setSelectedFolderId('all')}
            >
              📂 Все файлы <span className="ml-1 text-xs text-slate-400">{total}</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              className={`w-full text-left rounded-xl px-2 py-1.5 text-sm transition-colors ${selectedFolderId === null ? 'bg-sky-50 text-sky-700' : 'text-slate-700 hover:bg-slate-50'}`}
              onClick={() => setSelectedFolderId(null)}
            >
              📄 Без папки
            </button>
          </li>
          {folders.map((f) => (
            <FolderNode
              key={f.id}
              folder={f}
              selectedId={typeof selectedFolderId === 'number' ? selectedFolderId : null}
              onSelect={(id) => setSelectedFolderId(id)}
              onRename={handleRenameFolder}
              onDelete={handleDeleteFolder}
              onAddChild={(pid) => { setNewFolderParent(pid) }}
            />
          ))}
        </ul>
      </aside>

      {/* Center: grid/list */}
      <main className="min-w-0">
        {/* Toolbar */}
        <div className={workspacePanelClassName + ' mb-4'}>
          <div className="flex flex-wrap items-center gap-3">
            <input
              className={workspaceInputClassName + ' flex-1 min-w-40 !py-2'}
              placeholder="Поиск по имени или alt…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-sky-300"
              value={fileTypeFilter}
              onChange={(e) => setFileTypeFilter(e.target.value)}
            >
              <option value="">Все типы</option>
              <option value="image">Изображения</option>
              <option value="video">Видео</option>
              <option value="document">Документы</option>
            </select>
            <div className="flex rounded-xl border border-slate-200 overflow-hidden">
              <button type="button" className={`px-3 py-2 text-sm transition ${viewMode === 'grid' ? 'bg-sky-50 text-sky-700' : 'bg-white text-slate-600 hover:bg-slate-50'}`} onClick={() => setViewMode('grid')}>⊞</button>
              <button type="button" className={`px-3 py-2 text-sm transition ${viewMode === 'list' ? 'bg-sky-50 text-sky-700' : 'bg-white text-slate-600 hover:bg-slate-50'}`} onClick={() => setViewMode('list')}>☰</button>
            </div>
            <button
              type="button"
              className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-medium text-sky-700 hover:bg-sky-100 transition"
              onClick={() => fileInputRef.current?.click()}
            >
              ↑ Загрузить
            </button>
            <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx" className="hidden" onChange={onFileInputChange} />
          </div>

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 pt-2 border-t border-slate-100 mt-2">
              <span className="text-xs text-slate-500">Выбрано: {selectedIds.size}</span>
              <button type="button" className="text-xs text-rose-600 hover:underline" onClick={() => void bulkDelete()}>Удалить выбранные</button>
              <button type="button" className="text-xs text-slate-500 hover:underline" onClick={clearSelection}>Снять</button>
              <button type="button" className="text-xs text-slate-500 hover:underline" onClick={selectAll}>Выбрать все</button>
            </div>
          )}

          {uploading && (
            <div className="pt-2 mt-2 border-t border-slate-100">
              <div className="text-xs text-slate-500 mb-1">Загрузка файлов…</div>
              <UploadProgressBar current={uploadProgress.current} total={uploadProgress.total} />
            </div>
          )}
        </div>

        {/* Drop zone + grid */}
        <div
          className={`relative rounded-[24px] border-2 transition ${dragging ? 'border-sky-400 bg-sky-50' : 'border-transparent'}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          {dragging && (
            <div className="absolute inset-0 flex items-center justify-center rounded-[22px] bg-sky-50/80 z-10">
              <span className="text-sky-600 font-semibold text-lg">Отпустите для загрузки</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-40 text-slate-400 text-sm">Загрузка…</div>
          ) : items.length === 0 ? (
            <div className={workspaceEmptyClassName + ' text-center py-10'}>
              Нет файлов. Загрузите файлы через кнопку или перетащите сюда.
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-6">
              {items.map((item) => (
                <GridCard
                  key={item.id}
                  item={item}
                  selected={selectedIds.has(item.id)}
                  active={activeItem?.id === item.id}
                  onSelect={toggleSelect}
                  onClick={() => { setActiveItem(item); setEditingItem(null) }}
                />
              ))}
            </div>
          ) : (
            <div className="grid gap-1">
              {items.map((item) => (
                <ListRow
                  key={item.id}
                  item={item}
                  selected={selectedIds.has(item.id)}
                  active={activeItem?.id === item.id}
                  onSelect={toggleSelect}
                  onClick={() => { setActiveItem(item); setEditingItem(null) }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <button type="button" disabled={page <= 1} className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm text-slate-600 disabled:opacity-40" onClick={() => setPage((p) => p - 1)}>←</button>
            <span className="text-sm text-slate-600">{page} / {totalPages}</span>
            <button type="button" disabled={page >= totalPages} className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm text-slate-600 disabled:opacity-40" onClick={() => setPage((p) => p + 1)}>→</button>
          </div>
        )}
      </main>

      {/* Right: details */}
      <aside className={workspacePanelClassName + ' self-start'}>
        {!activeItem ? (
          <div className="text-sm text-slate-400 py-4 text-center">Выберите файл для просмотра</div>
        ) : (
          <div className="grid gap-4">
            {/* Preview */}
            {activeItem.file_type === 'image' && (
              <div className="rounded-[18px] overflow-hidden bg-slate-100 flex items-center justify-center aspect-square">
                <img
                  src={activeItem.large_url ?? activeItem.url}
                  alt={activeItem.alt_text ?? ''}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            )}
            {activeItem.file_type === 'video' && (
              <div className="rounded-[18px] overflow-hidden bg-slate-100">
                <video src={activeItem.url} controls className="w-full" />
              </div>
            )}
            {activeItem.file_type === 'document' && (
              <div className="flex items-center justify-center h-24 rounded-[18px] bg-slate-100">
                <span className="text-4xl">📄</span>
              </div>
            )}

            <div className="grid gap-1">
              <p className="text-sm font-semibold text-slate-800 break-all">{activeItem.original_filename}</p>
              <p className="text-xs text-slate-500">{fileTypeLabel(activeItem.file_type)} · {formatBytes(activeItem.size)}</p>
              {activeItem.width && activeItem.height && (
                <p className="text-xs text-slate-500">{activeItem.width} × {activeItem.height} px</p>
              )}
              <p className="text-xs text-slate-400">{new Date(activeItem.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            </div>

            {/* Copy URLs */}
            {activeItem.file_type === 'image' && (
              <div className="grid gap-2">
                <CopyButton label="Скопировать URL (large)" url={activeItem.large_url ?? activeItem.url} copiedKey="large" copied={copied} onCopy={copyUrl} />
                <CopyButton label="Скопировать URL (thumb)" url={activeItem.thumb_url ?? activeItem.url} copiedKey="thumb" copied={copied} onCopy={copyUrl} />
                <CopyButton label="Скопировать URL (original)" url={activeItem.url} copiedKey="orig" copied={copied} onCopy={copyUrl} />
              </div>
            )}
            {activeItem.file_type !== 'image' && (
              <CopyButton label="Скопировать URL" url={activeItem.url} copiedKey="orig" copied={copied} onCopy={copyUrl} />
            )}

            {/* Edit */}
            {editingItem ? (
              <div className="grid gap-3">
                <label className="grid gap-1">
                  <span className="text-xs font-semibold text-slate-600">Alt-текст</span>
                  <input className={workspaceInputClassName + ' !py-2 text-sm'} value={editingItem.altText} onChange={(e) => setEditingItem({ ...editingItem, altText: e.target.value })} placeholder="Описание изображения" />
                </label>
                <label className="grid gap-1">
                  <span className="text-xs font-semibold text-slate-600">Описание</span>
                  <textarea className={workspaceInputClassName + ' !py-2 text-sm resize-none h-20'} value={editingItem.description} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} placeholder="Заметка или описание" />
                </label>
                <div className="flex gap-2">
                  <button type="button" className="flex-1 rounded-xl bg-sky-600 px-3 py-2 text-sm text-white hover:bg-sky-700 transition" onClick={() => void saveEdit()}>Сохранить</button>
                  <button type="button" className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 transition" onClick={() => setEditingItem(null)}>Отмена</button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 transition"
                  onClick={() => setEditingItem({ altText: activeItem.alt_text ?? '', description: activeItem.description ?? '' })}
                >
                  ✎ Редактировать
                </button>
                <button
                  type="button"
                  className="rounded-xl border border-rose-200 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 transition"
                  onClick={() => void deleteItem(activeItem)}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  )
}

// ── Grid card ──────────────────────────────────────────────────────────────

function GridCard({
  item,
  selected,
  active,
  onSelect,
  onClick,
}: {
  item: MediaItem
  selected: boolean
  active: boolean
  onSelect: (id: number, e: React.MouseEvent) => void
  onClick: () => void
}) {
  return (
    <div
      className={`relative rounded-[18px] overflow-hidden border cursor-pointer transition select-none ${
        active ? 'border-sky-400 shadow-[0_0_0_2px_rgba(14,165,233,0.3)]' : selected ? 'border-sky-300' : 'border-slate-200 hover:border-slate-300'
      }`}
      onClick={onClick}
    >
      <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
        {item.file_type === 'image' && item.thumb_url ? (
          <img src={item.thumb_url} alt={item.alt_text ?? ''} className="w-full h-full object-cover" loading="lazy" />
        ) : item.file_type === 'video' ? (
          <span className="text-3xl">🎬</span>
        ) : (
          <span className="text-3xl">📄</span>
        )}
      </div>
      <div className="px-2 py-1.5 bg-white">
        <p className="text-xs text-slate-600 truncate">{item.original_filename}</p>
      </div>
      <button
        type="button"
        className={`absolute top-1.5 left-1.5 h-5 w-5 rounded-full border text-xs flex items-center justify-center transition ${
          selected ? 'border-sky-400 bg-sky-400 text-white' : 'border-slate-300 bg-white text-transparent hover:border-sky-300'
        }`}
        onClick={(e) => onSelect(item.id, e)}
      >
        ✓
      </button>
    </div>
  )
}

// ── List row ───────────────────────────────────────────────────────────────

function ListRow({
  item,
  selected,
  active,
  onSelect,
  onClick,
}: {
  item: MediaItem
  selected: boolean
  active: boolean
  onSelect: (id: number, e: React.MouseEvent) => void
  onClick: () => void
}) {
  return (
    <div
      className={`flex items-center gap-3 rounded-xl px-3 py-2 cursor-pointer transition border ${
        active ? 'border-sky-200 bg-sky-50' : selected ? 'border-sky-100 bg-sky-50/50' : 'border-transparent hover:bg-slate-50'
      }`}
      onClick={onClick}
    >
      <button type="button" className={`h-5 w-5 rounded-full border text-xs flex items-center justify-center transition flex-none ${selected ? 'border-sky-400 bg-sky-400 text-white' : 'border-slate-300 bg-white text-transparent'}`} onClick={(e) => onSelect(item.id, e)}>✓</button>
      <div className="h-10 w-10 flex-none rounded-[10px] overflow-hidden bg-slate-100 flex items-center justify-center">
        {item.file_type === 'image' && item.thumb_url ? (
          <img src={item.thumb_url} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : item.file_type === 'video' ? (
          <span>🎬</span>
        ) : (
          <span>📄</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700 truncate">{item.original_filename}</p>
        <p className="text-xs text-slate-400">{formatBytes(item.size)}{item.width ? ` · ${item.width}×${item.height}` : ''}</p>
      </div>
      <span className="text-xs text-slate-400 flex-none">{new Date(item.created_at).toLocaleDateString('ru-RU')}</span>
    </div>
  )
}

// ── Copy button ────────────────────────────────────────────────────────────

function CopyButton({ label, url, copiedKey, copied, onCopy }: {
  label: string; url: string; copiedKey: string; copied: string | null
  onCopy: (url: string, key: string) => void
}) {
  const isCopied = copied === copiedKey
  return (
    <button
      type="button"
      className={`w-full rounded-xl border px-3 py-2 text-xs font-medium transition text-left truncate ${
        isCopied ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-sky-50 hover:text-sky-700'
      }`}
      title={url}
      onClick={() => onCopy(url, copiedKey)}
    >
      {isCopied ? '✓ Скопировано' : label}
    </button>
  )
}
